import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Badge, DataTable, Input, Modal } from '../../../components/ui';
import { Search, Building2, KeyRound, Power, RefreshCw, RotateCcw, Plus, Trash2, Settings2 } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { adminService, type BancaCreateInput, type BancaLimitesUpdate } from '../services/adminService';

interface BancaItem {
  id: string;
  nombre: string;
  codigo: string;
  activo: boolean;
  max_usuarios: number;
  max_locales: number;
  limite_apuesta_maxima: number;
  locales: number;
  vendedores: number;
  usuario?: string | null;
}

export const BancasPage: React.FC = () => {
  const [bancas, setBancas] = useState<BancaItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // Modal credenciales
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBanca, setSelectedBanca] = useState<BancaItem | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);

  // Modal crear banca
  const [crearModalOpen, setCrearModalOpen] = useState(false);
  const [nuevaBanca, setNuevaBanca] = useState<BancaCreateInput>({
    nombre: '', codigo: '', username: '', password: '',
    max_usuarios: 5, max_locales: 3,
  });
  const [savingBanca, setSavingBanca] = useState(false);

  // Modal límites
  const [limitesModalOpen, setLimitesModalOpen] = useState(false);
  const [limitesBanca, setLimitesBanca] = useState<BancaItem | null>(null);
  const [limitesForm, setLimitesForm] = useState<BancaLimitesUpdate>({ max_usuarios: 0, max_locales: 0, limite_apuesta_maxima: 999999999 });
  const [savingLimites, setSavingLimites] = useState(false);

  const cargarBancas = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<BancaItem[]>('/usuarios/admin/bancas');
      setBancas(data);
    } catch (e: any) {
      setError(e.message || 'No se pudieron cargar las bancas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarBancas();
  }, []);

  const generarPasswordRapida = (length = 10) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let value = '';
    for (let i = 0; i < length; i += 1) {
      value += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return value;
  };

  const filteredBancas = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return bancas;
    return bancas.filter((b) =>
      b.nombre.toLowerCase().includes(q) ||
      b.codigo.toLowerCase().includes(q) ||
      (b.usuario || '').toLowerCase().includes(q)
    );
  }, [bancas, searchTerm]);

  // ── Credenciales ──
  const abrirModalCredenciales = (banca: BancaItem) => {
    setSelectedBanca(banca);
    setUsername(banca.usuario || '');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setModalOpen(true);
  };

  const guardarCredenciales = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBanca) return;

    const body: Record<string, string> = {};
    const newUsername = username.trim();
    if (newUsername && newUsername !== (selectedBanca.usuario || '')) body.username = newUsername;
    if (password) {
      if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
      if (password !== confirmPassword) { setError('Las contraseñas no coinciden'); return; }
      body.password = password;
    }
    if (Object.keys(body).length === 0) { setModalOpen(false); return; }

    setSavingCreds(true);
    setError('');
    try {
      await fetchAPI(`/usuarios/admin/bancas/${selectedBanca.id}/credenciales`, { method: 'PATCH', body: JSON.stringify(body) });
      setModalOpen(false);
      await cargarBancas();
    } catch (e: any) {
      setError(e.message || 'No se pudieron actualizar credenciales');
    } finally {
      setSavingCreds(false);
    }
  };

  // ── Toggle estado ──
  const toggleEstado = async (banca: BancaItem) => {
    setActionLoadingId(banca.id);
    setError('');
    try {
      await fetchAPI(`/usuarios/admin/bancas/${banca.id}/estado`, { method: 'PATCH', body: JSON.stringify({ activo: !banca.activo }) });
      await cargarBancas();
    } catch (e: any) {
      setError(e.message || 'No se pudo cambiar estado');
    } finally {
      setActionLoadingId(null);
    }
  };

  // ── Reset password rápida ──
  const resetPasswordRapida = async (banca: BancaItem) => {
    const ok = window.confirm(`¿Restablecer contraseña rápida para ${banca.nombre}?`);
    if (!ok) return;
    const nuevaPassword = generarPasswordRapida(10);
    setActionLoadingId(banca.id);
    setError('');
    try {
      await fetchAPI(`/usuarios/admin/bancas/${banca.id}/credenciales`, { method: 'PATCH', body: JSON.stringify({ password: nuevaPassword }) });
      window.prompt('Contraseña autogenerada (cópiela y entréguela):', nuevaPassword);
      await cargarBancas();
    } catch (e: any) {
      setError(e.message || 'No se pudo restablecer contraseña');
    } finally {
      setActionLoadingId(null);
    }
  };

  // ── Crear banca ──
  const handleCrearBanca = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nuevaBanca.nombre.trim() || !nuevaBanca.codigo.trim() || !nuevaBanca.username.trim() || !nuevaBanca.password) {
      setError('Todos los campos son requeridos');
      return;
    }
    if (nuevaBanca.password.length < 6) { setError('Contraseña mínimo 6 caracteres'); return; }

    setSavingBanca(true);
    setError('');
    try {
      await adminService.crearBanca(nuevaBanca);
      setCrearModalOpen(false);
      setNuevaBanca({ nombre: '', codigo: '', username: '', password: '', max_usuarios: 5, max_locales: 3 });
      await cargarBancas();
    } catch (e: any) {
      setError(e.message || 'No se pudo crear la banca');
    } finally {
      setSavingBanca(false);
    }
  };

  // ── Eliminar banca (cascada) ──
  const handleEliminar = async (banca: BancaItem) => {
    const msg = `¿ELIMINAR "${banca.nombre}" (${banca.codigo})?\n\nEsto borrará en cascada:\n• Todos sus usuarios (${banca.vendedores} vendedores)\n• Todos sus locales (${banca.locales})\n• Sus suscripciones y pagos\n\nEsta acción NO se puede deshacer.`;
    if (!window.confirm(msg)) return;
    const code = window.prompt(`Escriba el código "${banca.codigo}" para confirmar:`);
    if (code !== banca.codigo) { setError('Código incorrecto, eliminación cancelada'); return; }

    setActionLoadingId(banca.id);
    setError('');
    try {
      const result = await adminService.eliminarBanca(banca.id);
      alert(`Banca "${banca.nombre}" eliminada.\n\n• ${result.usuarios_eliminados} usuarios\n• ${result.locales_eliminados} locales\n• ${result.suscripciones_eliminadas} suscripciones\n• ${result.pagos_eliminados} pagos`);
      await cargarBancas();
    } catch (e: any) {
      setError(e.message || 'No se pudo eliminar la banca');
    } finally {
      setActionLoadingId(null);
    }
  };

  // ── Límites ──
  const abrirModalLimites = (banca: BancaItem) => {
    setLimitesBanca(banca);
    setLimitesForm({
      max_usuarios: banca.max_usuarios,
      max_locales: banca.max_locales,
      limite_apuesta_maxima: banca.limite_apuesta_maxima,
    });
    setError('');
    setLimitesModalOpen(true);
  };

  const guardarLimites = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!limitesBanca) return;
    setSavingLimites(true);
    setError('');
    try {
      await adminService.actualizarLimites(limitesBanca.id, limitesForm);
      setLimitesModalOpen(false);
      await cargarBancas();
    } catch (e: any) {
      setError(e.message || 'No se pudieron actualizar los límites');
    } finally {
      setSavingLimites(false);
    }
  };

  // ── Tabla ──
  const columns = [
    {
      key: 'banca',
      header: 'Banca',
      render: (b: BancaItem) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${b.activo ? 'bg-primary-100' : 'bg-gray-100'}`}>
            <Building2 className={b.activo ? 'text-primary-600' : 'text-gray-400'} size={20} />
          </div>
          <div>
            <p className="font-medium text-gray-900">{b.nombre}</p>
            <p className="text-xs text-gray-500">{b.codigo}</p>
          </div>
        </div>
      ),
    },
    { key: 'usuario', header: 'Usuario', render: (b: BancaItem) => <span>{b.usuario || '-'}</span> },
    { key: 'locales', header: 'Locales', render: (b: BancaItem) => <span className="font-medium">{b.locales} / {b.max_locales}</span> },
    { key: 'limite_apuesta_maxima', header: 'Máx. apuesta', render: (b: BancaItem) => <span className="font-medium">₡{Number(b.limite_apuesta_maxima || 0).toLocaleString()}</span> },
    {
      key: 'vendedores',
      header: 'Vendedores',
      render: (b: BancaItem) => (
        <span className={`font-medium ${b.vendedores >= b.max_usuarios ? 'text-danger-600' : ''}`}>
          {b.vendedores} / {b.max_usuarios}
        </span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (b: BancaItem) => (
        <Badge variant={b.activo ? 'success' : 'neutral'}>
          {b.activo ? 'Activa' : 'Inactiva'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (b: BancaItem) => (
        <div className="flex items-center gap-1">
          <button type="button" title="Límites" onClick={() => abrirModalLimites(b)}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" disabled={actionLoadingId === b.id}>
            <Settings2 size={16} />
          </button>
          <button type="button" title="Restablecer contraseña rápida" onClick={() => resetPasswordRapida(b)}
            className="p-2 text-gray-500 hover:text-success-600 hover:bg-success-50 rounded-lg" disabled={actionLoadingId === b.id}>
            <RotateCcw size={16} />
          </button>
          <button type="button" title="Editar usuario/contraseña" onClick={() => abrirModalCredenciales(b)}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" disabled={actionLoadingId === b.id}>
            <KeyRound size={16} />
          </button>
          <button type="button" title={b.activo ? 'Desactivar' : 'Activar'} onClick={() => toggleEstado(b)}
            className="p-2 text-gray-500 hover:text-warning-600 hover:bg-warning-50 rounded-lg" disabled={actionLoadingId === b.id}>
            <Power size={16} />
          </button>
          <button type="button" title="Eliminar banca" onClick={() => handleEliminar(b)}
            className="p-2 text-gray-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg" disabled={actionLoadingId === b.id}>
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const total = bancas.length;
  const activas = bancas.filter((b) => b.activo).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Bancas</h1>
          <p className="text-gray-500">Crear, editar límites, credenciales y eliminar bancas</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={() => { setError(''); setCrearModalOpen(true); }}>
            <Plus size={16} className="mr-1" /> Agregar Banca
          </Button>
          <button onClick={cargarBancas} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Refrescar">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><p className="text-sm text-gray-500">Total Bancas</p><p className="text-2xl font-bold">{total}</p></Card>
        <Card><p className="text-sm text-gray-500">Activas</p><p className="text-2xl font-bold text-success-600">{activas}</p></Card>
        <Card><p className="text-sm text-gray-500">Inactivas</p><p className="text-2xl font-bold text-gray-500">{total - activas}</p></Card>
      </div>

      {/* Search */}
      <Card>
        <Input placeholder="Buscar por banca, código o usuario..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} leftIcon={<Search size={18} />} />
      </Card>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      {/* Table */}
      <Card padding="none">
        <DataTable columns={columns} data={filteredBancas} keyExtractor={(b: BancaItem) => b.id} isLoading={loading} emptyMessage="No hay bancas registradas" />
      </Card>

      {/* ── Modal: Crear Banca ── */}
      <Modal isOpen={crearModalOpen} onClose={() => setCrearModalOpen(false)} title="Agregar Nueva Banca" size="md">
        <form onSubmit={handleCrearBanca} className="space-y-4">
          <Input label="Nombre de la banca" value={nuevaBanca.nombre} onChange={(e) => setNuevaBanca({ ...nuevaBanca, nombre: e.target.value })} required />
          <Input label="Código (único)" value={nuevaBanca.codigo} onChange={(e) => setNuevaBanca({ ...nuevaBanca, codigo: e.target.value.toUpperCase() })} required placeholder="Ej: BCA001" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Usuario" value={nuevaBanca.username} onChange={(e) => setNuevaBanca({ ...nuevaBanca, username: e.target.value })} required />
            <Input label="Contraseña" type="password" value={nuevaBanca.password} onChange={(e) => setNuevaBanca({ ...nuevaBanca, password: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Máx. vendedores" type="number" value={String(nuevaBanca.max_usuarios)} onChange={(e) => setNuevaBanca({ ...nuevaBanca, max_usuarios: parseInt(e.target.value) || 1 })} min={1} />
            <Input label="Máx. locales" type="number" value={String(nuevaBanca.max_locales)} onChange={(e) => setNuevaBanca({ ...nuevaBanca, max_locales: parseInt(e.target.value) || 1 })} min={1} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setCrearModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingBanca}>Crear Banca</Button>
          </div>
        </form>
      </Modal>

      {/* ── Modal: Credenciales ── */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selectedBanca ? `Credenciales: ${selectedBanca.nombre}` : 'Editar credenciales'} size="md">
        <form onSubmit={guardarCredenciales} className="space-y-4">
          <Input label="Usuario" value={username} onChange={(e) => setUsername(e.target.value)} required />
          <Input label="Nueva contraseña (opcional)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Input label="Confirmar contraseña" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={!password} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingCreds}>Guardar cambios</Button>
          </div>
        </form>
      </Modal>

      {/* ── Modal: Límites ── */}
      <Modal isOpen={limitesModalOpen} onClose={() => setLimitesModalOpen(false)} title={limitesBanca ? `Límites: ${limitesBanca.nombre}` : 'Editar límites'} size="md">
        <form onSubmit={guardarLimites} className="space-y-4">
          {limitesBanca && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              <p>Uso actual: <strong>{limitesBanca.vendedores}</strong> vendedores, <strong>{limitesBanca.locales}</strong> locales</p>
            </div>
          )}
          <Input label="Máx. vendedores" type="number" value={String(limitesForm.max_usuarios ?? 0)}
            onChange={(e) => setLimitesForm({ ...limitesForm, max_usuarios: parseInt(e.target.value) || 0 })} min={0} />
          <Input label="Máx. locales" type="number" value={String(limitesForm.max_locales ?? 0)}
            onChange={(e) => setLimitesForm({ ...limitesForm, max_locales: parseInt(e.target.value) || 0 })} min={0} />
          <Input label="Máximo por apuesta" type="number" value={String(limitesForm.limite_apuesta_maxima ?? 999999999)}
            onChange={(e) => setLimitesForm({ ...limitesForm, limite_apuesta_maxima: parseFloat(e.target.value) || 0 })} min={1} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setLimitesModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingLimites}>Guardar Límites</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BancasPage;
